import {Dispatch, ReactNode, SetStateAction} from 'react'
import {AtaDetalhe, EsferaCodigo, RespostasEntrevista} from '../types'
import {avaliarConformidade, ReqStatus, resumoConformidade} from '../compliance'
import {ESFERA_LABEL, fmtDate} from '../ui'

interface Props {
    ata: AtaDetalhe
    termoBuscado: string
    aderenteNome: string
    respostas: RespostasEntrevista
    setRespostas: Dispatch<SetStateAction<RespostasEntrevista>>
    evidencias: Record<string, string>
    setEvidencias: Dispatch<SetStateAction<Record<string, string>>>
    gerando: boolean
    onGerar: () => void
    sugerindo: boolean
    onSugerirDemanda: () => void
    onVoltar: () => void
}

const REQ_ICON: Record<ReqStatus, string> = {
    ok: '<path d="M20 6 9 17l-5-5"/>',
    risco: '<line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>',
    pendente: '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>',
    lembrete: '<line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>',
    na: '<line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>',
}

function autoCheck(ok: boolean, okMsg: string, failMsg: string) {
    return (
        <div className={`callout ${ok ? 'callout--success' : 'callout--danger'}`} style={{margin: 0}}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                dangerouslySetInnerHTML={{__html: ok ? '<path d="M20 6 9 17l-5-5"/>' : '<circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>'}} />
            <div>{ok ? okMsg : failMsg}</div>
        </div>
    )
}

function Bloco({n, title, req, children}: {n: number; title: string; req: string; children: ReactNode}) {
    return (
        <div className="bloco">
            <div className="bloco__head">
                <span className="bloco__num">{n}</span>
                <div><div className="bloco__title">{title}</div><div className="bloco__req">{req}</div></div>
            </div>
            <div className="bloco__body">{children}</div>
        </div>
    )
}

export default function Entrevista(
    {ata, termoBuscado, aderenteNome, respostas, setRespostas, evidencias, setEvidencias, gerando, onGerar, sugerindo, onSugerirDemanda, onVoltar}: Props
) {
    const e = respostas
    const aderente = e.esfera_aderente
    // Merge sempre no estado MAIS RECENTE (forma funcional): callbacks assíncronos
    // — ex.: a sugestão de IA da demanda — não podem sobrescrever toggles que o
    // usuário marcou enquanto a IA gerava (era o bug do "precisa apertar de novo").
    const set = (patch: Partial<RespostasEntrevista>) => setRespostas((prev) => ({...prev, ...patch}))

    const toggleEv = (id: string, nome: string) =>
        setEvidencias((prev) => ({...prev, [id]: prev[id] ? '' : nome}))
    const setQtd = (item: number, v: number) =>
        set({quantidades_desejadas: {...e.quantidades_desejadas, [String(item)]: v}})

    const reqs = avaliarConformidade(ata, respostas, evidencias)
    const {ok, total, pct} = resumoConformidade(reqs)
    const vedado = aderente === 'F' && ['E', 'M', 'D'].includes(ata.esfera_codigo)
    const vigente = ['VIGENTE', 'A_VENCER'].includes(ata.status_vigencia)

    return (
        <div>
            <header className="page-header">
                <div className="crumb">
                    <a onClick={onVoltar}>Detalhe da ata</a>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                    <span>Entrevista de adesão</span>
                </div>
                <div className="header-row"><h1 className="lg-h2">Entrevista de justificativa</h1></div>
                <p className="lg-text-muted" style={{marginBottom: 0}}>
                    Ata {ata.numero_ata || '—'} · {ata.orgao} · órgão aderente <strong>{ESFERA_LABEL[aderente]}</strong>. Responda os 6 blocos; o painel à direita acompanha cada requisito legal.
                </p>
            </header>

            <div className="page-content" style={{maxWidth: 1180}}>
                <div className="callout callout--info" style={{marginBottom: 22}}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="10"/></svg>
                    <div><span className="callout__title">A força da justificativa vem da evidência, não da redação.</span> Cada pergunta atende a um requisito legal. Este painel é uma prévia — a verificação completa acontece quando você gera a justificativa.</div>
                </div>

                <div className="interview-grid">
                    <div>
                        <Bloco n={1} title="Identificação e necessidade" req="Requisito 2 — motivação compatível com a demanda real (TCU Ac. 2630/2024)">
                            <div className="lg-field">
                                <label className="lg-label lg-label--required">Esfera do órgão aderente (define a vedação do art. 86, §8º)</label>
                                <select className="lg-select" value={e.esfera_aderente}
                                    onChange={(ev) => set({esfera_aderente: ev.target.value as EsferaCodigo})}>
                                    <option value="F">Federal</option>
                                    <option value="E">Estadual</option>
                                    <option value="M">Municipal</option>
                                    <option value="D">Distrital</option>
                                </select>
                            </div>
                            <div className="lg-field">
                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 6}}>
                                    <label className="lg-label lg-label--required" style={{marginBottom: 0}}>Demanda concreta do órgão aderente</label>
                                    <button type="button" className="lg-btn lg-btn--secondary lg-btn--sm" onClick={onSugerirDemanda} disabled={sugerindo}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z"/></svg>
                                        {sugerindo ? 'Sugerindo…' : 'Sugerir com Licito.Guru IA'}
                                    </button>
                                </div>
                                <textarea className="lg-textarea" value={e.demanda_descricao}
                                    onChange={(ev) => set({demanda_descricao: ev.target.value})}
                                    placeholder="Descreva a necessidade e a destinação (ex.: substituição de 180 notebooks em fim de vida útil…)" />
                            </div>
                            <div className="toggle-row">
                                <div className={`toggle${e.demanda_prevista_pca ? ' is-on' : ''}`} onClick={() => set({demanda_prevista_pca: !e.demanda_prevista_pca})}></div>
                                <span style={{fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)'}}>A demanda está prevista no PCA / planejamento</span>
                            </div>
                            <div className="toggle-row">
                                <div className={`toggle${e.alega_urgencia ? ' is-on' : ''}`} onClick={() => set({alega_urgencia: !e.alega_urgencia})}></div>
                                <span style={{fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)'}}>Há urgência / risco de desabastecimento ou descontinuidade</span>
                            </div>
                            {e.alega_urgencia && (
                                <div className="lg-field">
                                    <label className="lg-label lg-label--required">Evidência da urgência (referência do documento)</label>
                                    <input className="lg-input" type="text" value={evidencias['urgencia_desabastecimento'] || ''}
                                        onChange={(ev) => setEvidencias({...evidencias, urgencia_desabastecimento: ev.target.value})}
                                        placeholder="Ex.: Relatório de estoque 03/2026" />
                                </div>
                            )}
                        </Bloco>

                        <Bloco n={2} title="Vantajosidade da adesão" req="Requisito 1 — o ponto mais sensível (TCU Ac. 420/2018 e 1794/2023)">
                            <div className="callout callout--warn" style={{margin: 0}}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                                <div><span className="callout__title">Comparar só com os preços da própria ata é insuficiente.</span>O TCU exige pesquisa de preços própria e atualizada para aferir que o preço registrado é compatível com o mercado.</div>
                            </div>
                            <div className="toggle-row">
                                <div className={`toggle${e.declara_preco_compativel ? ' is-on' : ''}`} onClick={() => set({declara_preco_compativel: !e.declara_preco_compativel})}></div>
                                <span style={{fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)'}}>Declaro que o preço registrado é compatível com o parâmetro de mercado</span>
                            </div>
                        </Bloco>

                        <Bloco n={3} title="Limites quantitativos" req="Requisito 5 — 50% individual (§3º) e 200% coletivo (§4º)">
                            <div>
                                {ata.itens.length === 0 && <p style={{fontSize: 'var(--fs-sm)', color: 'var(--text-tertiary)', margin: 0}}>Esta ata não tem itens cadastrados.</p>}
                                {ata.itens.map((it) => {
                                    const limite = Math.floor(it.qtd_registrada * 0.5)
                                    const desejado = e.quantidades_desejadas[String(it.numero_item)] || 0
                                    const excede = desejado > limite
                                    return (
                                        <div key={it.numero_item} style={{display: 'grid', gridTemplateColumns: '1fr 130px', gap: 12, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--border-subtle)'}}>
                                            <div>
                                                <div style={{fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)' as any, color: 'var(--text-primary)'}}>Item {it.numero_item} — {it.descricao}</div>
                                                <div style={{fontSize: 'var(--fs-xs)', color: excede ? 'var(--feedback-danger)' : 'var(--text-tertiary)'}}>
                                                    Registrado: {it.qtd_registrada.toLocaleString('pt-BR')} · limite 50%: {limite.toLocaleString('pt-BR')} {excede ? '· ⚠ excede o teto individual' : ''}
                                                </div>
                                            </div>
                                            <input className="lg-input" type="number" min={0} value={desejado}
                                                onChange={(ev) => setQtd(it.numero_item, parseInt(ev.target.value || '0', 10))}
                                                style={excede ? {borderColor: 'var(--feedback-danger)'} : undefined} />
                                        </div>
                                    )
                                })}
                            </div>
                            <div className={`callout ${ata.saldo_status === 'DISPONIVEL' ? 'callout--info' : 'callout--warn'}`} style={{margin: 0}}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                                <div>Saldo coletivo (200%, §4º): <strong>{ata.saldo_status === 'DISPONIVEL' ? 'disponível' : ata.saldo_status === 'ESGOTADO' ? 'esgotado' : 'indeterminado'}</strong>. Confirme com o gerenciador antes de aderir.</div>
                            </div>
                        </Bloco>

                        <Bloco n={4} title="Vigência e esfera" req="Requisitos 6 e 7 — validações automáticas">
                            <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
                                {autoCheck(vigente, `Ata vigente até ${fmtDate(ata.vigencia_fim)}.`, 'Ata fora de vigência.')}
                                {autoCheck(!vedado, `Compatibilidade de esfera OK: aderente ${ESFERA_LABEL[aderente]} × gerenciador ${ESFERA_LABEL[ata.esfera_codigo] || ata.esfera}.`, `Vedação art. 86, §8º — federal não adere a ata ${(ESFERA_LABEL[ata.esfera_codigo] || '').toLowerCase()}.`)}
                            </div>
                        </Bloco>

                        <Bloco n={5} title="Anuências" req="Requisitos 3 e 4 — gerenciador e fornecedor (a obter)">
                            <div className="callout callout--info" style={{margin: 0}}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                                <div>
                                    <span className="callout__title">O que você precisará juntar ao processo</span>
                                    Antes de formalizar a adesão, obtenha: (1) a <strong>autorização do órgão gerenciador</strong> da ata e (2) o <strong>aceite do fornecedor registrado</strong> em fornecer ao órgão não participante. Enquanto não constarem dos autos, a justificativa é uma <strong>minuta condicionada</strong> a essas anuências.
                                </div>
                            </div>
                            <div className="toggle-row">
                                <div className={`toggle${e.ciente_anuencias ? ' is-on' : ''}`} onClick={() => set({ciente_anuencias: !e.ciente_anuencias})}></div>
                                <span style={{fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)'}}>Estou ciente de que preciso obter a autorização do gerenciador e o aceite do fornecedor antes de formalizar a adesão</span>
                            </div>
                        </Bloco>

                        <Bloco n={6} title="Edital de origem" req="Requisito 7 — cláusula de adesão (a confirmar)">
                            <div className="callout callout--info" style={{margin: 0}}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
                                <div>
                                    <span className="callout__title">Confirme no edital de origem</span>
                                    A adesão por não participantes depende de o edital/ata de origem admiti-la. Rode o <strong>Analisador de Editais</strong> sobre o documento de origem para localizar a cláusula. Enquanto não confirmado, fica como ponto pendente da minuta.
                                </div>
                            </div>
                            <div className="toggle-row">
                                <div className={`toggle${e.ciente_edital ? ' is-on' : ''}`} onClick={() => set({ciente_edital: !e.ciente_edital})}></div>
                                <span style={{fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)'}}>Estou ciente de que devo confirmar no edital de origem se há previsão de adesão por não participantes</span>
                            </div>
                        </Bloco>
                    </div>

                    <div>
                        <div className="conformidade">
                            <div className="conformidade__head">
                                <div className="conformidade__title">Painel de conformidade</div>
                                <div className="conformidade__sub">Checklist legal — prévia (verificação final ao gerar a justificativa)</div>
                                <div className="progress-ring">
                                    <div className="progress-ring__bar"><div className="progress-ring__fill" style={{width: `${pct}%`}}></div></div>
                                    <div className="progress-ring__count">{ok}/{total}</div>
                                </div>
                            </div>
                            <ul className="req-list">
                                {reqs.map((r) => (
                                    <li key={r.id} className={`req-item req-item--${r.status}`}>
                                        <span className="req-item__icon">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
                                                dangerouslySetInnerHTML={{__html: REQ_ICON[r.status]}} />
                                        </span>
                                        <div><div className="req-item__txt">{r.titulo}</div><div className="req-item__hint">{r.base_legal} · {r.hint}</div></div>
                                    </li>
                                ))}
                            </ul>
                            <div style={{padding: '14px 16px', borderTop: '1px solid var(--border-subtle)'}}>
                                <button className="lg-btn lg-btn--brand lg-btn--block" disabled={gerando} onClick={onGerar}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                    {gerando ? 'Gerando…' : 'Gerar justificativa (1 documento)'}
                                </button>
                                <p style={{fontSize: 'var(--fs-xs)', color: 'var(--text-tertiary)', textAlign: 'center', marginTop: 8}}>Gera mesmo com pendências — em modo minuta marcada.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
